import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import {
  APPROVE_SOCIAL_POST_VERSION,
  GET_SOCIAL_POST_HISTORY,
  GET_TINYCMS_HISTORY,
  PUBLISH_TINYCMS_CONTENT,
  RESTORE_SOCIAL_POST_VERSION,
  RESTORE_TINYCMS_VERSION,
} from '../../graphql';
import { Loader } from '../common/PageLoader';
import { blockedNote, waitingChecks } from './historyChecks';
import {
  approvalLine,
  approveLabel,
  compareVersions,
  HistoryData,
  HistoryVersion,
  KIND_NAME,
  minuteTime,
  noDifferenceLine,
  pendingNote,
  restoreCopy,
  shortHash,
  statusOf,
  tagOf,
  versionOption,
} from './historyWords';
import { showInkToast } from './InkToast';
import { VersionLook } from './VersionLook';

/**
 * History: one view for pages, articles and social posts (design "Version
 * history and rollback"). Every version and who made it, who approved it, which
 * one is live; See this version, what it looked like (VersionLook.tsx); Compare
 * between any two; Restore, which adds a new working copy that goes through
 * approval and never publishes. Full screen, over whatever
 * opened it. Approving goes through what the item's own screen uses:
 * publishTinycmsContent for a page or article, the post's approval for a post.
 * Open checks show beside Approve; one that stops it leaves approving to the
 * item's own screen (historyChecks.ts).
 */

const Tag = ({ tag }: { tag: { text: string; tone: string } | null }) =>
  tag ? (
    <span className="hv-tag" data-tone={tag.tone}>
      {tag.text}
    </span>
  ) : null;

export function HistoryView({
  kind,
  id,
  onClose,
  onChanged,
  onOpenChecks,
  origin = null,
}: {
  kind: 'article' | 'page' | 'post';
  id: string;
  /** Where it came from, before version 1: an article from a feed. */
  origin?: { source: string; at?: string | null } | null;
  onClose: () => void;
  /** A restore or an approval changed the item: its own screen refreshes. */
  onChanged?: () => unknown;
  /** Close, and show the item's Checks. */
  onOpenChecks?: () => void;
}) {
  const { accountId } = useAccount();
  // A post is Reach's own (and its own access: a Campaign-role person may open theirs).
  const isPost = kind === 'post';
  const { data, loading, error, refetch } = useQuery<{
    tinycmsHistory?: HistoryData;
    socialPostHistory?: HistoryData;
  }>(isPost ? GET_SOCIAL_POST_HISTORY : GET_TINYCMS_HISTORY, {
    variables: isPost ? { id } : { accountId, kind, id },
    skip: !isPost && !accountId,
    fetchPolicy: 'network-only',
  });
  const [restore, { loading: restoring }] = useMutation(
    isPost ? RESTORE_SOCIAL_POST_VERSION : RESTORE_TINYCMS_VERSION
  );
  const [publish, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [approvePost, { loading: approving }] = useMutation(APPROVE_SOCIAL_POST_VERSION);
  const history = data?.socialPostHistory || data?.tinycmsHistory;
  const versions = useMemo(() => history?.versions || [], [history]);

  const [selV, setSelV] = useState<number | null>(null);
  const [mode, setMode] = useState<'view' | 'compare'>('view');
  const [seeing, setSeeing] = useState(false);
  const [confirmV, setConfirmV] = useState<number | null>(null);
  const [fromV, setFromV] = useState<number | null>(null);
  const [toV, setToV] = useState<number | null>(null);
  const [problem, setProblem] = useState('');
  const busy = restoring || publishing || approving;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const byV = (v: number | null) => versions.find((x) => x.v === v) || null;
  const newest = versions[versions.length - 1] || null;
  const liveV = history?.liveV || history?.scheduledV || 0;
  // Opens on what's waiting for a yes, if anything; else on what's live.
  const sel = byV(selV) || versions.find((v) => v.pending) || byV(liveV) || newest;
  const many = versions.length > 1;
  const showing = many ? mode : 'view';
  // Brought in while live: it had gone live from the feed, on its own date.
  const broughtLive = versions.some((v) => /^Brought into/i.test(v.what));

  const previous = sel ? versions[versions.indexOf(sel) - 1] || null : null;
  const fromVersion =
    byV(fromV) ||
    (sel && sel === newest && many ? byV(liveV !== sel.v ? liveV : 0) || previous : sel);
  const toVersion = byV(toV) || (liveV && liveV !== fromVersion?.v ? byV(liveV) : newest);

  const openCompare = (from: HistoryVersion, to: HistoryVersion | null) => {
    setFromV(from.v);
    setToV(to?.v ?? null);
    setConfirmV(null);
    setSeeing(false);
    setMode('compare');
  };
  const askRestore = (v: HistoryVersion) => {
    setSelV(v.v);
    setConfirmV(v.v);
    setSeeing(false);
    setMode('view');
    setProblem('');
  };

  const doRestore = async (x: HistoryVersion) => {
    setProblem('');
    try {
      const { data: res } = await restore({
        variables: isPost ? { id, version: x.v } : { accountId, kind, id, version: x.v },
      });
      const n = (res?.restoreSocialPostVersion || res?.restoreTinycmsVersion)?.version;
      await refetch();
      setConfirmV(null);
      setSelV(n ?? null);
      setFromV(null);
      setToV(null);
      setMode('view');
      showInkToast(`Restored v${x.v} as v${n}. It’s waiting for approval.`);
      void onChanged?.();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const doApprove = async (v: HistoryVersion) => {
    if (!history) return;
    setProblem('');
    try {
      if (kind === 'post') {
        await approvePost({ variables: { id, version: v.v, contentHash: v.hash } });
        showInkToast(
          history.approve.action === 'new_post'
            ? `Approved v${v.v}. It goes out as a new post.`
            : `Approved v${v.v}. It’s scheduled.`
        );
      } else {
        const { data: res } = await publish({
          variables: {
            accountId,
            kind,
            id,
            action: history.approve.action,
            contentHash: v.hash,
            scheduledFor: history.approve.scheduledFor || undefined,
          },
        });
        const r = res?.publishTinycmsContent;
        if (!r?.ok) {
          setProblem(
            r?.refused === 'blocking_findings'
              ? 'This version has checks to fix before it can go live. Close History and fix them under Checks, then approve it there or here.'
              : r?.message || 'That was refused. Try again.'
          );
          await refetch();
          return;
        }
        showInkToast(
          history.approve.action === 'schedule'
            ? `Approved v${v.v}. It’s scheduled.`
            : `Approved v${v.v}. It’s live now.`
        );
      }
      await refetch();
      void onChanged?.();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };

  const confirming = byV(confirmV);
  const confirmCopy = confirming && history ? restoreCopy(confirming, history) : null;
  const compared =
    fromVersion && toVersion && fromVersion !== toVersion
      ? compareVersions(fromVersion, toVersion)
      : null;
  const checks = history ? waitingChecks(history) : null;
  const compareWith =
    sel && (liveV && liveV !== sel.v ? byV(liveV) : sel === newest ? previous : newest);

  return createPortal(
    <div className="hv" role="dialog" aria-label="History" data-screen-label="History">
      <div className="hv-head">
        <strong className="hv-title">History</strong>
        {history ? (
          <span className="hv-sub">
            {KIND_NAME[kind]}
            {history.channel ? ` · ${history.channel}` : ''} · {history.title}
          </span>
        ) : null}
        {many ? (
          <div className="hv-modes">
            {(
              [
                ['view', 'Versions'],
                ['compare', 'Compare'],
              ] as const
            ).map(([m, label]) => (
              <button
                type="button"
                key={m}
                data-on={showing === m ? 'true' : undefined}
                onClick={() => {
                  setMode(m);
                  setConfirmV(null);
                  setSeeing(false);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
        <button type="button" className="hv-close" aria-label="Close history" onClick={onClose}>
          ×
        </button>
      </div>

      {!history ? (
        <div className="hv-empty">
          {loading ? (
            <Loader label="Loading history" className="ink-site-row-desc" />
          ) : (
            <p role="alert">{error?.message || 'History isn’t available right now.'}</p>
          )}
        </div>
      ) : (
        <div className="hv-grid">
          <div className="hv-list">
            <div className="hv-count">
              {versions.length} {versions.length === 1 ? 'VERSION' : 'VERSIONS'}
            </div>
            {[...versions].reverse().map((v) => {
              const line = approvalLine(v, history);
              return (
                <button
                  type="button"
                  key={v.v}
                  className="hv-row"
                  aria-pressed={v === sel && showing === 'view'}
                  data-on={v === sel && showing === 'view' ? 'true' : undefined}
                  onClick={() => {
                    setConfirmV(null);
                    if (showing === 'compare') setFromV(v.v);
                    else setSelV(v.v);
                  }}
                >
                  <span className="hv-row-v">v{v.v}</span>
                  <span className="hv-row-main">
                    <span className="hv-row-what">
                      {v.what}
                      <Tag tag={tagOf(v, history)} />
                    </span>
                    <span className="hv-row-by">
                      Made by {v.madeBy.name} · {v.madeBy.label}
                    </span>
                    <span className="hv-row-appr" data-tone={line.tone}>
                      {line.text}
                    </span>
                    <span className="hv-row-when">{minuteTime(v.at)}</span>
                  </span>
                </button>
              );
            })}
            {origin ? (
              <div className="hv-row hv-row-origin">
                <span className="hv-row-v" aria-hidden="true">
                  ·
                </span>
                <span className="hv-row-main">
                  <span className="hv-row-what">
                    {broughtLive ? 'Went live from your feed' : 'Arrived from your feed as a draft'}
                  </span>
                  <span className="hv-row-by">{origin.source}</span>
                  {broughtLive && origin.at ? (
                    <span className="hv-row-when">{minuteTime(origin.at)}</span>
                  ) : null}
                </span>
              </div>
            ) : null}
            <p className="hv-foot">
              Restoring never deletes anything. It adds a new working copy on top, which goes
              through approval like any other change.
            </p>
          </div>

          <div className="hv-main">
            <div
              className="hv-main-in"
              data-wide={seeing && showing === 'view' && !isPost ? 'true' : undefined}
            >
              {problem ? (
                <p className="hv-problem" role="alert">
                  {problem}
                </p>
              ) : null}
              {confirming && confirmCopy ? (
                <div className="hv-confirm" data-screen-label="History · Restore">
                  <strong>{confirmCopy.title}</strong>
                  <p>{confirmCopy.body}</p>
                  <div className="hv-actions">
                    <button type="button" className="ink-btn" onClick={() => setConfirmV(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ink-btn ink-btn-primary"
                      disabled={busy}
                      onClick={() => void doRestore(confirming)}
                    >
                      {restoring ? 'Restoring…' : confirmCopy.go}
                    </button>
                  </div>
                </div>
              ) : null}

              {showing === 'view' && sel && seeing ? (
                <>
                  <div className="hv-sel-head">
                    <h2>
                      v{sel.v} · {sel.what}
                    </h2>
                    <Tag tag={tagOf(sel, history)} />
                  </div>
                  <VersionLook
                    kind={kind}
                    id={id}
                    version={sel}
                    history={history}
                    actions={
                      <>
                        <button type="button" className="ink-btn" onClick={() => setSeeing(false)}>
                          Back to details
                        </button>
                        {sel !== newest ? (
                          <button type="button" className="ink-btn" onClick={() => askRestore(sel)}>
                            Restore this version
                          </button>
                        ) : null}
                      </>
                    }
                  />
                </>
              ) : null}

              {showing === 'view' && sel && !seeing ? (
                <>
                  <div className="hv-sel-head">
                    <h2>
                      v{sel.v} · {sel.what}
                    </h2>
                    <Tag tag={tagOf(sel, history)} />
                  </div>
                  {sel.pending ? (
                    <div className="hv-pending" data-screen-label="History · Waiting for approval">
                      <span>{pendingNote(sel, history)}</span>
                      {checks?.rows.length ? (
                        <div className="hv-checks">
                          {checks.rows.map((r) => (
                            <div className="ink-check" key={r.text}>
                              <span className="ink-check-icon" style={{ color: r.color }}>
                                {r.icon}
                              </span>
                              <span>{r.text}</span>
                              <span className="ink-check-sev" style={{ color: r.color }}>
                                {r.sev}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {history.canApprove === false ? (
                        <span className="hv-pending-who">
                          Someone on the site approves this one.
                        </span>
                      ) : checks?.blocked ? (
                        <>
                          <span className="hv-pending-who hv-pending-why">
                            {blockedNote(checks, history)}
                          </span>
                          <button
                            type="button"
                            className="ink-btn"
                            onClick={isPost ? onClose : onOpenChecks || onClose}
                          >
                            {isPost ? 'Back to the post' : 'Go to Checks'}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="ink-btn ink-btn-primary"
                          disabled={busy}
                          onClick={() => void doApprove(sel)}
                        >
                          {publishing || approving ? 'Approving…' : approveLabel(sel, history)}
                        </button>
                      )}
                    </div>
                  ) : null}
                  <div className="hv-facts">
                    {[
                      ['Made by', `${sel.madeBy.name} (${sel.madeBy.label})`],
                      ['Approval', approvalLine(sel, history).text],
                      ['Saved', minuteTime(sel.at)],
                      ['Status', statusOf(sel, history)],
                    ].map(([label, value]) => (
                      <div className="hv-fact" key={label}>
                        <span>{label}</span>
                        <span>{value}</span>
                      </div>
                    ))}
                    <div className="hv-fact">
                      <span>Version</span>
                      <span className="hv-hash">{shortHash(sel.hash)}</span>
                    </div>
                  </div>
                  <div className="hv-actions">
                    {sel !== newest ? (
                      <button
                        type="button"
                        className="ink-btn ink-btn-primary"
                        onClick={() => askRestore(sel)}
                      >
                        Restore this version
                      </button>
                    ) : null}
                    <button type="button" className="ink-btn" onClick={() => setSeeing(true)}>
                      See this version
                    </button>
                    {many && compareWith ? (
                      <button
                        type="button"
                        className="ink-btn"
                        onClick={() => openCompare(sel, compareWith)}
                      >
                        Compare with v{compareWith.v}
                        {compareWith.v === history.liveV
                          ? ` (${history.liveWord.toLowerCase()})`
                          : compareWith.v === history.scheduledV
                            ? ' (scheduled)'
                            : ''}
                      </button>
                    ) : null}
                    {sel === newest && many ? (
                      <span className="hv-note">
                        This is the newest version. Pick an earlier one to restore it.
                      </span>
                    ) : null}
                  </div>
                  <div className="hv-kicker">In this version</div>
                  {sel.fields.map((f) => (
                    <div className="hv-field" key={f.label}>
                      <div className="hv-field-head">{f.label}</div>
                      <div className="hv-field-body" data-empty={f.value ? undefined : 'true'}>
                        {f.value === null ? 'Not recorded on this version' : f.value || 'Empty'}
                      </div>
                    </div>
                  ))}
                </>
              ) : null}

              {showing === 'compare' && fromVersion && toVersion ? (
                <>
                  <div className="hv-pickers">
                    <select
                      aria-label="Compare from"
                      value={fromVersion.v}
                      onChange={(e) => setFromV(Number(e.target.value))}
                    >
                      {[...versions].reverse().map((v) => (
                        <option key={v.v} value={v.v}>
                          {versionOption(v, history)}
                        </option>
                      ))}
                    </select>
                    <span className="hv-arrow">→</span>
                    <select
                      aria-label="Compare to"
                      value={toVersion.v}
                      onChange={(e) => setToV(Number(e.target.value))}
                    >
                      {[...versions].reverse().map((v) => (
                        <option key={v.v} value={v.v}>
                          {versionOption(v, history)}
                        </option>
                      ))}
                    </select>
                    {fromVersion !== newest ? (
                      <button
                        type="button"
                        className="ink-btn"
                        onClick={() => askRestore(fromVersion)}
                      >
                        Restore v{fromVersion.v}…
                      </button>
                    ) : null}
                  </div>
                  <div className="hv-cmp-head">
                    <strong>
                      v{fromVersion.v} → v{toVersion.v}
                    </strong>
                    {compared?.changed.length ? (
                      <span className="hv-cmp-stats">
                        {compared.changed.length}{' '}
                        {compared.changed.length === 1 ? 'field' : 'fields'} changed
                        {compared.added || compared.removed
                          ? ` · ${compared.added} words added, ${compared.removed} removed`
                          : ''}
                      </span>
                    ) : null}
                    <span className="hv-legend">
                      <del>removed</del>
                      <ins>added</ins>
                    </span>
                  </div>
                  {!compared || !compared.changed.length ? (
                    <div className="hv-nodiff">
                      {!compared
                        ? 'Pick two different versions.'
                        : noDifferenceLine(fromVersion, toVersion, compared.unknown)}
                    </div>
                  ) : (
                    compared.changed.map((f) => (
                      <div className="hv-field" key={f.label}>
                        <div className="hv-field-head">
                          {f.label}
                          <span className="hv-field-stat">{f.stat}</span>
                        </div>
                        {f.segs.length ? (
                          <div className="hv-field-body">
                            {f.segs.map((s, i) =>
                              s.k === 'add' ? (
                                <ins key={i}>{s.t}</ins>
                              ) : s.k === 'del' ? (
                                <del key={i}>{s.t}</del>
                              ) : (
                                <span key={i}>{s.t}</span>
                              )
                            )}
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                  {compared?.changed.length && compared.same.length ? (
                    <p className="hv-note">Unchanged: {compared.same.join(', ')}.</p>
                  ) : null}
                  {compared?.unknown.length ? (
                    <p className="hv-note">
                      Not recorded on the older version: {compared.unknown.join(', ')}.
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

/** Where History opens from an article's side panel or a post's. */
export function HistoryLink({
  onOpen,
  label = 'History →',
  disabled = false,
}: {
  onOpen: () => void;
  label?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="rc-panel-rowlink"
      onClick={onOpen}
      disabled={disabled}
      title={disabled ? 'Finish editing first' : undefined}
    >
      {label}
    </button>
  );
}
